import PropTypes from 'prop-types'
import { Icon } from '@iconify/react'
import { capitalCase } from 'change-case'
import videoFill from '@iconify/icons-eva/video-fill'
import phoneFill from '@iconify/icons-eva/phone-fill'
import moreVerticalFill from '@iconify/icons-eva/more-vertical-fill'
import arrowIosForwardFill from '@iconify/icons-eva/arrow-ios-forward-fill'
// material
import { experimentalStyled as styled } from '@material-ui/core/styles'
import {
  Box,
  Link,
  Avatar,
  Typography,
  IconButton,
  Autocomplete,
  Stack,
  Button,
  TextField,
  Tooltip,
  Popover,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from '@material-ui/core'
// utils
//
import BadgeStatus from 'src/components/BadgeStatus'
import { MChip, MIconButton } from 'src/components/@material-extend'
import { fToNow } from 'src/utils/formatTime'
import defaltProfile from '../../../../assets/defaultAvatar.png'
import { useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { DOMAIN, REST_API_END_POINT, STATUS_OPTIONS } from 'src/constants/DefaultValues'
import { createChatSocket } from 'src/Helpers/createSocket'
import { ArrowBackIosNew, PersonAddAlt, Send } from '@material-ui/icons'
import { WA } from '../whatsappTheme'
import SearchNotFound from 'src/components/SearchNotFound'
import match from 'autosuggest-highlight/match'
import parse from 'autosuggest-highlight/parse'
import { useSnackbar } from 'notistack'
import axios from 'axios'
import { useTheme } from '@mui/material'
// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
  flexShrink: 0,
  minHeight: 72,
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 2),
  background: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
}))

const AutocompleteStyle = styled('div')(({ theme }) => ({
  '& .MuiAutocomplete-root': {
    width: 280,
    '&.Mui-focused .MuiAutocomplete-inputRoot': {
      boxShadow: theme.customShadows.z8,
    },
  },
  '& .MuiAutocomplete-inputRoot': {
    transition: theme.transitions.create('box-shadow', {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.shorter,
    }),
    '& fieldset': {
      borderWidth: `1px !important`,
    },
  },
  '& .MuiAutocomplete-paper': {
    maxHeight: '50vh', // Set a maximum height
    overflowY: 'auto', // Enable vertical scrolling
  },
}))

// ----------------------------------------------------------------------

OneAvatar.propTypes = {
  participants: PropTypes.array,
}

function OneAvatar({ participants, compact }) {
  const participant = participants

  if (participant === undefined) {
    return null
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Box sx={{ position: 'relative' }}>
        <Avatar
          src={
            typeof participant?.profile_pic === 'string'
              ? participant.profile_pic
              : defaltProfile
          }
          alt={participant?.full_name}
          sx={compact ? { width: 40, height: 40 } : undefined}
        />
        <BadgeStatus
          status={participant?.status}
          sx={{ position: 'absolute', right: 2, bottom: 2 }}
        />
      </Box>
      <Box sx={{ ml: 2 }}>
        <Typography variant="subtitle2" sx={{ color: compact ? WA.text : 'text.primary', fontWeight: compact ? 700 : 500 }}>
          {participant?.full_name}
          {participant?.user_type === 'client' && (
            <span> (Client)</span>
          )}
        </Typography>
        <Typography variant="body2" sx={{ color: compact ? WA.meta : 'text.secondary', fontSize: compact ? 12 : undefined }}>
          {/* {participant?.active !== 'N' ? "Online" : fToNow(participant?.lastActivity)} */}
          {participant?.chat_status
            ? STATUS_OPTIONS?.find(
                (item) => item.id == participant?.chat_status,
              )?.label
            : participant?.last_activity
            ? fToNow(moment.unix(participant?.last_activity))
            : 'Offline'}
          {/* Offline */}
        </Typography>
      </Box>
    </Box>
  )
}

GroupAvatar.propTypes = {
  contact: PropTypes.array,
}

function GroupAvatar({ contact }) {
  let participants = contact?.participants
  let groupName = contact?.group_name || 'Unnamed Group'
  const theme = useTheme()
  const [anchorEl, setAnchorEl] = useState(null)
  const getName = (participant) =>
    participant?.full_name ||
    [participant?.first_name, participant?.last_name].filter(Boolean).join(' ').trim() ||
    'User'

  const handleOpenPeople = (event) => {
    event.preventDefault()
    setAnchorEl(event.currentTarget)
  }

  const handleClosePeople = () => {
    setAnchorEl(null)
  }

  const extraCount = Math.max(0, (participants?.length || 0) - 2)

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {participants?.slice(0, extraCount > 0 ? 2 : 3).map((participant, index) => {
          const name = getName(participant)
          return (
            <Tooltip key={participant.user_id || participant.id} title={name} arrow>
              <Avatar
                alt={name}
                src={
                  typeof participant?.profile_pic === 'string'
                    ? participant.profile_pic
                    : defaltProfile
                }
                sx={{
                  width: 32,
                  height: 32,
                  ml: index === 0 ? 0 : -1,
                  border: '2px solid #fff',
                  zIndex: (participants?.length || 0) - index,
                  cursor: 'default',
                }}
              />
            </Tooltip>
          )
        })}
        {extraCount > 0 && (
          <Tooltip title={`${extraCount} more`} arrow>
            <Box
              sx={{
                width: 32,
                height: 32,
                ml: -1,
                borderRadius: '50%',
                bgcolor: WA.header,
                color: '#fff',
                fontSize: 12,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid #fff',
                zIndex: 0,
                cursor: 'default',
              }}
            >
              +{extraCount}
            </Box>
          </Tooltip>
        )}
      </Box>
      <Box sx={{ ml: 2, minWidth: 0 }}>
        <Typography variant="subtitle2" noWrap>
          {groupName}
        </Typography>
        <Link
          variant="body2"
          underline="none"
          component="button"
          type="button"
          onClick={handleOpenPeople}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', color: theme.palette.text.primary }}>
            {participants?.length} persons
            <Icon icon={arrowIosForwardFill} />
          </Box>
        </Link>
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleClosePeople}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{
            sx: {
              mt: 0.5,
              minWidth: 240,
              maxHeight: 320,
              borderRadius: '12px',
              boxShadow: WA.shadow,
              border: `1px solid ${WA.border}`,
            },
          }}
        >
          <Typography variant="subtitle2" sx={{ px: 2, pt: 1.5, pb: 0.5, color: WA.text }}>
            People in this chat
          </Typography>
          <List dense disablePadding sx={{ py: 0.5 }}>
            {(participants || []).map((participant) => {
              const name = getName(participant)
              return (
                <ListItem key={participant.user_id || participant.id}>
                  <ListItemAvatar sx={{ minWidth: 44 }}>
                    <Avatar
                      alt={name}
                      src={
                        typeof participant?.profile_pic === 'string'
                          ? participant.profile_pic
                          : defaltProfile
                      }
                      sx={{ width: 32, height: 32 }}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={name}
                    primaryTypographyProps={{ variant: 'subtitle2', noWrap: true }}
                  />
                </ListItem>
              )
            })}
          </List>
        </Popover>
      </Box>
    </Box>
  )
}

ChatHeaderDetailSignWise.propTypes = {
  contact: PropTypes.array,
}

export default function ChatHeaderDetailSignWise({
  compact = false,
  setOnChatScreen,
  contact,
  handleRefresh,
  allContacts,
  handleOpensidebar,
  setSelectedContact,
  setCompose,
  showEndChat = false,
  onEndChat,
  endingChat = false,
  maximized = false,
  onToggleMaximize,
  notificationsEnabled = true,
  onToggleNotifications,
  onDownloadTranscript,
  ...other
}) {
  const [isGroup, setIsGroup] = useState(false)
  const [showAddMembers, setShowAddMembers] = useState(false)
  const [members, setMembers] = useState([])
  const [selectedMembers, setSelectedMembers] = useState([])
  const [query, setQuery] = useState('')
  const user = JSON.parse(localStorage.getItem('user'))
  const { enqueueSnackbar } = useSnackbar()
  const theme = useTheme()

  const getMemberName = (item) =>
    item?.full_name ||
    [item?.first_name, item?.last_name].filter(Boolean).join(' ').trim() ||
    'Unknown'

  const filteredContacts = useMemo(() => {
    return (allContacts || []).filter((item) => {
      if (!item?.user_id || item.conversation_type === 'group') return false
      if (!query.trim()) return true
      return getMemberName(item).toLowerCase().includes(query.trim().toLowerCase())
    })
  }, [allContacts, query])

  useEffect(() => {
    const group = contact?.conversation_type === 'group'
    setIsGroup(group)
    const participantIds = new Set(
      (contact?.participants || []).map((participant) => Number(participant.user_id)),
    )
    if (contact?.user_id) participantIds.add(Number(contact.user_id))
    if (user?.user_id) participantIds.add(Number(user.user_id))
    participantIds.add(39)
    setMembers(
      filteredContacts.filter((item) => !participantIds.has(Number(item.user_id))),
    )
  }, [contact, filteredContacts])
  console.log('contact@$^', contact)

  const handleToggleAddMembers = () => {
    setShowAddMembers(!showAddMembers)
    setSelectedMembers([])
  }

  const handleAddMemberToGroup = async () => {
    if (selectedMembers?.length === 0) {
      enqueueSnackbar('Please select at least one member', {
        variant: 'warning',
      })
      return
    }
    try {
      const response = await axios.post(
        compact
          ? `${REST_API_END_POINT}chat/add-helpdesk-members`
          : `${REST_API_END_POINT}chat/add-members-to-group`,
        compact
          ? {
              originalUserId: contact?.user_id || contact?.created_by,
              conversationId:
                contact?.conversation_type === 'group'
                  ? contact?.conversation_id || contact?.id
                  : null,
              userId: user?.user_id,
              selectedMembers,
            }
          : {
              conversationId: contact?.conversation_id,
              userId: user?.user_id,
              selectedMembers,
            },
      )
      let res = response?.data
      if (res) {
        enqueueSnackbar(res.message, {
          variant: res.status === 1 ? 'success' : 'error',
        })
        if (res.status === 1) {
          if (compact && typeof setSelectedContact === 'function') {
            setSelectedContact({
              ...contact,
              conversation_id: res.conversation_id || res.conversationId,
              conversation_type: 'group',
              new_conversation_id: res.new_conversation_id,
              group_name: res.group_name || contact?.group_name || contact?.full_name,
              created_by: res.created_by,
              participants: res.participants || [],
              user_id: null,
              chat_started: 1,
            })
            try {
              const socket = createChatSocket()
              const pendingInvites = res.pendingInvites || []
              if (pendingInvites.length) {
                pendingInvites.forEach((invite) => {
                  socket.emit('message', {
                    conversationId: res.new_conversation_id,
                    message: invite.message,
                    sender: user,
                    senderId: user?.user_id,
                    participants: res.participants || [],
                    selectedChatLogId: null,
                    domain: DOMAIN,
                    contentType: 'system',
                  })
                })
              } else {
                socket.emit('message', {
                  conversationId: res.new_conversation_id,
                  message: res.message,
                  sender: user,
                  senderId: user?.user_id,
                  participants: res.participants || [],
                  selectedChatLogId: null,
                  domain: DOMAIN,
                  contentType: 'system',
                })
              }
            } catch (socketError) {
              console.log('Error notifying added members', socketError)
            }
          }
          if (typeof handleRefresh === 'function') handleRefresh()
          setShowAddMembers(false)
          setSelectedMembers([])
          setQuery('')
        }
      }
    } catch (error) {
      console.error('Error adding members to chat:', error)
      if (error.response) {
        enqueueSnackbar(
          error.response.data?.message || 'Something went wrong!',
          { variant: 'error' },
        )
      } else {
        enqueueSnackbar('Error: Something went wrong!', { variant: 'error' })
      }
    }
  }

  const canAddMembers = compact ? showEndChat : isGroup

  return (
    <RootStyle
      {...other}
      sx={{
        flexWrap: compact ? 'wrap' : 'nowrap',
        rowGap: compact ? 1 : 0,
        minHeight: compact ? 64 : 72,
        py: compact && showAddMembers ? 1.25 : compact ? 1 : undefined,
        px: compact ? 2 : 2,
        bgcolor: compact ? '#fff' : 'background.paper',
        color: compact ? WA.text : 'text.primary',
        borderBottom: compact ? `1px solid ${WA.border}` : '1px solid rgba(0,0,0,0.08)',
      }}
    >
      <Tooltip title="Back to conversation list">
        <IconButton
          onClick={() => {
            setSelectedContact(null)
            setCompose(true)
            if (typeof setOnChatScreen === 'function') setOnChatScreen(true)
          }}
          sx={
            compact
              ? {
                  mr: 1,
                  color: WA.meta,
                  width: 36,
                  height: 36,
                  '&:hover': {
                    bgcolor: 'rgba(2, 125, 194, 0.08)',
                    color: WA.header,
                  },
                }
              : {
                  mr: 2,
                  backgroundColor:
                    theme.palette.mode === 'light'
                      ? 'rgba(2, 125, 194, 0.1)'
                      : 'rgba(2, 125, 194, 0.2)',
                  color: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor:
                      theme.palette.mode === 'light'
                        ? 'rgba(2, 125, 194, 0.15)'
                        : 'rgba(2, 125, 194, 0.3)',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }
          }
        >
          <ArrowBackIosNew fontSize={compact ? 'small' : 'medium'} />
        </IconButton>
      </Tooltip>
      {isGroup ? (
        <GroupAvatar contact={contact} />
      ) : (
        <OneAvatar participants={contact} compact={compact} />
      )}
      <Box sx={{ flexGrow: 1 }} />
      {showEndChat && (
        <Button
          type="button"
          size="small"
          onClick={onEndChat}
          disabled={endingChat}
          sx={{
            mr: compact ? 0 : 1,
            textTransform: 'none',
            fontWeight: 700,
            color: compact ? WA.meta : 'error.main',
            border: compact ? `1px solid ${WA.border}` : '1px solid #FF4842',
            borderRadius: compact ? 20 : 4,
            minWidth: 88,
            px: compact ? 1.75 : undefined,
            '&:hover': {
              bgcolor: compact ? 'rgba(255, 72, 66, 0.08)' : 'rgba(255, 72, 66, 0.08)',
              color: 'error.main',
              borderColor: '#FF4842',
            },
          }}
        >
          {endingChat ? 'Ending...' : 'End Chat'}
        </Button>
      )}
      {canAddMembers && (
        <Tooltip title="Add users">
          <MIconButton onClick={handleToggleAddMembers}>
            <PersonAddAlt
              htmlColor={
                showAddMembers
                  ? undefined
                  : compact
                  ? WA.header
                  : '#ffffff'
              }
              color={showAddMembers ? 'primary' : undefined}
            />
          </MIconButton>
        </Tooltip>
      )}
      {canAddMembers && showAddMembers && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{
            width: '100%',
            flexBasis: '100%',
            order: 1,
            mt: compact ? 1.25 : 0,
            pt: compact ? 1.25 : 0,
            borderTop: compact ? `1px solid ${WA.border}` : 'none',
          }}
        >
          <AutocompleteStyle>
            <Autocomplete
              multiple
              size="small"
              disablePortal
              popupIcon={null}
              noOptionsText={<SearchNotFound searchQuery={query} />}
              onChange={(event, newValue) => {
                setSelectedMembers(newValue)
              }}
              value={selectedMembers}
              inputValue={query}
              onInputChange={(event, newQuery) => setQuery(newQuery)}
              options={members}
              getOptionLabel={(contact) => getMemberName(contact)}
              renderOption={(props, contact, { inputValue }) => {
                const name = getMemberName(contact)
                const matches = match(name, inputValue)
                const parts = parse(name, matches)
                return (
                  <li {...props}>
                    <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                      <Avatar
                        alt={name}
                        src={contact.profile_pic || '/default-avatar.png'}
                      />
                      <Typography
                        variant="subtitle2"
                        noWrap
                        sx={{ ml: 2, minWidth: 0 }}
                      >
                        {parts.map((part, index) => (
                          <span
                            key={index}
                            style={{
                              color: part.highlight ? undefined : 'inherit',
                              fontWeight: part.highlight ? 700 : 500,
                            }}
                          >
                            {part.text}
                          </span>
                        ))}
                      </Typography>
                    </Box>
                  </li>
                )
              }}
              renderTags={(recipients, getTagProps) =>
                recipients.map((recipient, index) => (
                  <MChip
                    key={recipient.id || recipient.user_id}
                    size="small"
                    label={getMemberName(recipient)}
                    color="info"
                    avatar={
                      <Avatar
                        alt={getMemberName(recipient)}
                        src={recipient.profile_pic || '/default-avatar.png'}
                      />
                    }
                    {...getTagProps({ index })}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  label="Select members..."
                  {...params}
                  placeholder={
                    selectedMembers.length === 0 ? 'Recipients' : ''
                  }
                />
              )}
            />
          </AutocompleteStyle>
          <Button
            onClick={handleAddMemberToGroup}
            sx={{
              border: '1px dashed #2e434b',
              '&:hover': {
                backgroundColor: 'primary.main',
                color: 'common.white',
              },
            }}
          >
            <Send sx={{ fontSize: '1rem' }} />
          </Button>
        </Stack>
      )}
      {/* <MIconButton>
        <Icon icon={phoneFill} width={20} height={20} />
      </MIconButton>
      <MIconButton>
        <Icon icon={videoFill} width={20} height={20} />
      </MIconButton> */}
      {/* <MIconButton>
        <Icon icon={moreVerticalFill} width={20} height={20} />
      </MIconButton> */}
    </RootStyle>
  )
}
